/**
 * Network Schematic Component
 *
 * D3.js-based interactive power system one-line diagram.
 * Features:
 * - Force-directed or hierarchical layout
 * - Zoom and pan
 * - Bus voltage coloring
 * - Branch flow animation
 * - Generator/Load icons
 * - Tooltip on hover
 * - Click to select
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

// =============================================================================
// Types
// =============================================================================

interface Bus {
  id: number;
  name: string;
  base_kv: number;
  bus_type: 'slack' | 'pv' | 'pq';
  vm_pu?: number;
  va_deg?: number;
  p_mw?: number;
  q_mvar?: number;
  x?: number;
  y?: number;
}

interface Branch {
  id: number;
  from_bus: number;
  to_bus: number;
  name?: string;
  r_pu: number;
  x_pu: number;
  rate_mva?: number;
  p_from_mw?: number;
  q_from_mvar?: number;
  loading_percent?: number;
}

interface Generator {
  id: number;
  bus_id: number;
  name: string;
  pg_mw?: number;
  qg_mvar?: number;
  pmax_mw?: number;
  status: boolean;
}

interface Load {
  id: number;
  bus_id: number;
  name?: string;
  pd_mw: number;
  qd_mvar: number;
}

type PositionedBus = Bus & { x: number; y: number };
type Link = Branch & { source: PositionedBus; target: PositionedBus };

export interface NetworkData {
  buses: Bus[];
  branches: Branch[];
  generators: Generator[];
  loads: Load[];
}

interface NetworkSchematicProps {
  data: NetworkData;
  width?: number;
  height?: number;
  layout?: 'force' | 'hierarchical';
  showFlow?: boolean;
  showLabels?: boolean;
  showGenerators?: boolean;
  showLoads?: boolean;
  showLegend?: boolean;
  highlightOverloads?: boolean;
  lineWidthMode?: 'fixed' | 'loading';
  sizeByLoad?: boolean;
  onBusSelect?: (bus: Bus) => void;
  onBranchSelect?: (branch: Branch) => void;
  selectedBusId?: number;
  selectedBranchId?: number;
}

// =============================================================================
// Constants
// =============================================================================

const VOLTAGE_COLORS: Record<number, string> = {
  400: '#ff0000',  // 400kV - Red
  220: '#ff8000',  // 220kV - Orange
  110: '#ffff00',  // 110kV - Yellow
  66: '#00ff00',   // 66kV - Green
  33: '#00ffff',   // 33kV - Cyan
  11: '#0080ff',   // 11kV - Blue
  0.4: '#8000ff',  // LV - Purple
};

const BUS_TYPE_SHAPES = {
  slack: 'd3.symbolSquare',
  pv: 'd3.symbolCircle',
  pq: 'd3.symbolCircle',
};

// =============================================================================
// Helper Functions
// =============================================================================

function getVoltageColor(kv: number): string {
  const voltages = Object.keys(VOLTAGE_COLORS).map(Number).sort((a, b) => b - a);
  for (const v of voltages) {
    if (kv >= v * 0.9) {
      return VOLTAGE_COLORS[v];
    }
  }
  return '#808080';
}

function getVoltageDeviationColor(vm_pu: number): string {
  if (vm_pu === undefined) return '#808080';
  if (vm_pu < 0.95) return '#ef4444'; // Under-voltage - Red
  if (vm_pu > 1.05) return '#f59e0b'; // Over-voltage - Orange
  return '#22c55e'; // Normal - Green
}

function getLoadingColor(percent: number): string {
  if (percent === undefined) return '#808080';
  if (percent > 100) return '#ef4444';    // Overloaded - Red
  if (percent > 80) return '#f59e0b';     // High loading - Orange
  if (percent > 50) return '#eab308';     // Medium - Yellow
  return '#22c55e';                        // Normal - Green
}

// =============================================================================
// Component
// =============================================================================

export const NetworkSchematic: React.FC<NetworkSchematicProps> = ({
  data,
  width = 800,
  height = 600,
  layout = 'force',
  showFlow = true,
  showLabels = true,
  showGenerators = true,
  showLoads = true,
  showLegend = true,
  highlightOverloads = true,
  lineWidthMode = 'loading',
  sizeByLoad = false,
  onBusSelect,
  onBranchSelect,
  selectedBusId,
  selectedBranchId,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({ visible: false, x: 0, y: 0, content: '' });

  // Create D3 visualization
  useEffect(() => {
    if (!svgRef.current || !data.buses.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Set up zoom
    const g = svg.append('g');
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    svg.call(zoom);

    // Create bus node data with positions
    const nodes: PositionedBus[] = data.buses.map((bus, i) => ({
      ...bus,
      x: bus.x ?? (Math.cos(2 * Math.PI * i / data.buses.length) * 200 + width / 2),
      y: bus.y ?? (Math.sin(2 * Math.PI * i / data.buses.length) * 200 + height / 2),
    }));

    // Create link data
    const links = data.branches
      .map((branch) => ({
        ...branch,
        source: nodes.find((n) => n.id === branch.from_bus),
        target: nodes.find((n) => n.id === branch.to_bus),
      }))
      .filter((link): link is Link => Boolean(link.source && link.target));

    // Degree lookup for sizing
    const degreeLookup = new Map<number, number>();
    links.forEach((link) => {
      degreeLookup.set(link.source.id, (degreeLookup.get(link.source.id) || 0) + 1);
      degreeLookup.set(link.target.id, (degreeLookup.get(link.target.id) || 0) + 1);
    });

    const maxLoad = d3.max(nodes, (d) => Math.abs(d.p_mw || 0)) || 0;
    const maxDegree = d3.max(nodes, (d) => degreeLookup.get(d.id) || 0) || 1;
    const loadScale = d3.scaleSqrt().domain([0, Math.max(maxLoad, 1)]).range([10, 20]);
    const degreeScale = d3.scaleLinear().domain([0, maxDegree]).range([10, 16]);
    const xScale = d3.scalePoint()
      .domain(nodes.map((n) => n.id.toString()))
      .range([80, width - 80]);

    const getBandY = (kv: number) => {
      if (kv >= 220) return height * 0.2;
      if (kv >= 110) return height * 0.4;
      if (kv >= 66) return height * 0.6;
      return height * 0.8;
    };

    // Force simulation
    const simulation = d3.forceSimulation(nodes as d3.SimulationNodeDatum[])
      .force(
        'link',
        d3.forceLink(links as any)
          .id((d: any) => d.id)
          .distance(layout === 'hierarchical' ? 140 : 100)
      )
      .force('charge', d3.forceManyBody().strength(layout === 'hierarchical' ? -260 : -320))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30))
      .force(
        'x',
        layout === 'hierarchical'
          ? d3.forceX((d: any) => xScale(String(d.id)) || width / 2).strength(0.25)
          : d3.forceX(width / 2).strength(0.05)
      )
      .force(
        'y',
        layout === 'hierarchical'
          ? d3.forceY((d: any) => getBandY(d.base_kv)).strength(0.6)
          : d3.forceY(height / 2).strength(0.05)
      );

    // Draw branches (lines)
    const branchGroup = g.append('g').attr('class', 'branches');
    const widthScale = d3.scaleLinear().domain([0, 120]).range([2, 6]);
    const branchLines = branchGroup.selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', (d) => {
        const loading = d.loading_percent || 0;
        const isOverloaded = loading > 100;
        if (highlightOverloads && isOverloaded) return '#ef4444';
        return getLoadingColor(loading);
      })
      .attr('stroke-width', (d) => {
        const loading = d.loading_percent || 0;
        const baseWidth = lineWidthMode === 'loading' ? widthScale(loading) : 2.5;
        return d.id === selectedBranchId ? baseWidth + 2 : baseWidth;
      })
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', (d) => {
        const loading = d.loading_percent || 0;
        return loading > 100 ? '6,4' : null;
      })
      .attr('class', (d) => d.id === selectedBranchId ? 'selected' : '')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        onBranchSelect?.(d);
      })
      .on('mouseover', (event, d) => {
        const loading = d.loading_percent?.toFixed(1) || 'N/A';
        const power = d.p_from_mw?.toFixed(1) || 'N/A';
        setTooltip({
          visible: true,
          x: event.pageX,
          y: event.pageY,
          content: `${d.name || `Branch ${d.id}`}\nP: ${power} MW\nLoading: ${loading}%`,
        });
      })
      .on('mouseout', () => {
        setTooltip((t) => ({ ...t, visible: false }));
      });

    // Animated flow arrows
    const flowLinks = links
      .filter((l) => l.p_from_mw && Math.abs(l.p_from_mw) > 0.1)
      .map((l) => ({
        ...l,
        flowDirection: l.p_from_mw && l.p_from_mw < 0 ? -1 : 1,
      }));

    if (showFlow) {
      branchGroup.selectAll('path.flow-arrow')
        .data(flowLinks)
        .enter()
        .append('path')
        .attr('class', 'flow-arrow')
        .attr('fill', 'none')
        .attr('stroke', '#3b82f6')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '10,5')
        .attr('marker-end', 'url(#arrowhead)');
    }

    // Draw buses (nodes)
    const busGroup = g.append('g').attr('class', 'buses');
    const busNodes = busGroup.selectAll('g.bus')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'bus')
      .style('cursor', 'pointer')
      .call(d3.drag<any, any>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
      )
      .on('click', (event, d) => {
        event.stopPropagation();
        onBusSelect?.(d);
      })
      .on('mouseover', (event, d) => {
        const vm = d.vm_pu?.toFixed(4) || 'N/A';
        const va = d.va_deg?.toFixed(2) || 'N/A';
        const p = d.p_mw !== undefined ? d.p_mw.toFixed(1) : 'N/A';
        const q = d.q_mvar !== undefined ? d.q_mvar.toFixed(1) : 'N/A';
        setTooltip({
          visible: true,
          x: event.pageX,
          y: event.pageY,
          content: `${d.name}\n${d.base_kv}kV (${d.bus_type})\nVm: ${vm} pu\nVa: ${va} deg\nP: ${p} MW\nQ: ${q} MVAr`,
        });
      })
      .on('mouseout', () => {
        setTooltip((t) => ({ ...t, visible: false }));
      });

    // Bus circles
    busNodes.append('circle')
      .attr('r', (d) => {
        const base = d.bus_type === 'slack' ? 16 : 12;
        const dynamic = sizeByLoad
          ? loadScale(Math.abs(d.p_mw || 0))
          : degreeScale(degreeLookup.get(d.id) || 0);
        return Math.max(base, dynamic);
      })
      .attr('fill', (d) => getVoltageDeviationColor(d.vm_pu || 1.0))
      .attr('stroke', (d) => getVoltageColor(d.base_kv))
      .attr('stroke-width', (d) => d.id === selectedBusId ? 5 : 3)
      .attr('class', (d) => d.id === selectedBusId ? 'selected' : '');

    // Slack bus marker
    busNodes.filter((d) => d.bus_type === 'slack')
      .append('rect')
      .attr('x', -6)
      .attr('y', -6)
      .attr('width', 12)
      .attr('height', 12)
      .attr('fill', 'none')
      .attr('stroke', '#000')
      .attr('stroke-width', 2);

    // Generator icons
    const gens = showGenerators ? data.generators.filter((gen) => gen.status) : [];
    const genNodes = busGroup.selectAll('g.generator')
      .data(gens)
      .enter()
      .append('g')
      .attr('class', 'generator');

    genNodes.append('circle')
      .attr('r', 8)
      .attr('fill', '#22c55e')
      .attr('stroke', '#000')
      .attr('stroke-width', 1);

    genNodes.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 3)
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .text('G');

    // Load icons
    const loadNodes = busGroup.selectAll('g.load')
      .data(showLoads ? data.loads : [])
      .enter()
      .append('g')
      .attr('class', 'load');

    loadNodes.append('polygon')
      .attr('points', '-6,0 6,0 0,10')
      .attr('fill', '#f59e0b')
      .attr('stroke', '#000')
      .attr('stroke-width', 1);

    // Bus labels
    if (showLabels) {
      busNodes.append('text')
        .attr('dy', -20)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#fff')
        .text((d) => d.name);
    }

    // Arrow marker definition
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('path')
      .attr('d', 'M 0,-5 L 10 ,0 L 0,5')
      .attr('fill', '#3b82f6');

    // Simulation tick
    simulation.on('tick', () => {
      branchLines
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      busNodes.attr('transform', (d: any) => `translate(${d.x},${d.y})`);

      // Position generators relative to their buses
      genNodes.attr('transform', (d) => {
        const bus = nodes.find((n) => n.id === d.bus_id);
        return bus ? `translate(${(bus as any).x + 25},${(bus as any).y - 15})` : '';
      });

      // Position loads relative to their buses
      loadNodes.attr('transform', (d) => {
        const bus = nodes.find((n) => n.id === d.bus_id);
        return bus ? `translate(${(bus as any).x - 25},${(bus as any).y + 15})` : '';
      });

      // Update flow arrows
      if (showFlow) {
        g.selectAll('path.flow-arrow')
          .attr('d', (d: any) => {
            if (!d.source || !d.target) return '';
            const start = d.flowDirection === -1 ? d.target : d.source;
            const end = d.flowDirection === -1 ? d.source : d.target;
            return `M ${start.x},${start.y} L ${end.x},${end.y}`;
          });
      }
    });

    // Clear selection on background click
    svg.on('click', () => {
      onBusSelect?.(undefined as any);
      onBranchSelect?.(undefined as any);
    });

    return () => {
      simulation.stop();
    };
  }, [
    data,
    width,
    height,
    layout,
    showFlow,
    showLabels,
    showGenerators,
    showLoads,
    highlightOverloads,
    lineWidthMode,
    sizeByLoad,
    selectedBusId,
    selectedBranchId,
    onBusSelect,
    onBranchSelect,
  ]);

  return (
    <div className="relative bg-gray-900 rounded-lg overflow-hidden">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="w-full h-full"
      />

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="absolute bg-gray-800 text-white text-xs p-2 rounded shadow-lg pointer-events-none whitespace-pre-line z-50"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y + 10,
          }}
        >
          {tooltip.content}
        </div>
      )}

      {/* Legend */}
      {showLegend && (
        <div className="absolute bottom-2 left-2 bg-gray-800/90 p-2 rounded text-xs text-white">
          <div className="font-bold mb-1">Voltage Levels</div>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(VOLTAGE_COLORS).map(([kv, color]) => (
              <div key={kv} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span>{kv}kV</span>
              </div>
            ))}
          </div>
          <div className="font-bold mt-2 mb-1">Status</div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>Normal (0.95-1.05 pu)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span>High (&gt;1.05 pu)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span>Low (&lt;0.95 pu)</span>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="absolute top-2 right-2 flex gap-2">
        <button
          className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs"
          onClick={() => {
            const svg = d3.select(svgRef.current);
            svg.transition().duration(500).call(
              d3.zoom<SVGSVGElement, unknown>().transform as any,
              d3.zoomIdentity
            );
          }}
        >
          Reset Zoom
        </button>
        <button
          className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs"
          onClick={() => {
            // Export as SVG
            const svgElement = svgRef.current;
            if (svgElement) {
              const serializer = new XMLSerializer();
              const svgString = serializer.serializeToString(svgElement);
              const blob = new Blob([svgString], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'network-schematic.svg';
              a.click();
              URL.revokeObjectURL(url);
            }
          }}
        >
          Export SVG
        </button>
      </div>
    </div>
  );
};

export default NetworkSchematic;
